import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OpportunityMedia } from "./OpportunityMedia";
import { OpportunityHeader } from "./opportunity-details/OpportunityHeader";
import { RecruiterInfo } from "./opportunity-details/RecruiterInfo";
import { OpportunityDetails } from "./opportunity-details/OpportunityDetails";
import { OrganizationInfo } from "./opportunity-details/OrganizationInfo";

interface OpportunityDetailedViewProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  createdBy: {
    id: string;
    fullName: string | null;
    avatarUrl: string | null;
  };
}

export const OpportunityDetailedView = ({ 
  isOpen, 
  onClose, 
  opportunityId,
  createdBy 
}: OpportunityDetailedViewProps) => {
  const { data: recruiterProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['recruiter-profile', createdBy.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', createdBy.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!createdBy.id,
  });

  const { data: opportunity, isLoading: isLoadingOpportunity } = useQuery({
    queryKey: ['opportunity-details', opportunityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('opportunities')
        .select(`
          *,
          organization:organizations(
            name,
            description,
            location,
            website,
            type
          )
        `)
        .eq('id', opportunityId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const isLoading = isLoadingProfile || isLoadingOpportunity;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        ) : opportunity && (
          <>
            <OpportunityHeader 
              title={opportunity.title} 
              type={opportunity.type} 
            />

            <div className="space-y-6">
              <OpportunityMedia opportunityId={opportunityId} />

              <RecruiterInfo 
                createdBy={createdBy}
                recruiterProfile={recruiterProfile}
              />

              <OpportunityDetails
                description={opportunity.description}
                location={opportunity.location}
                compensation={opportunity.compensation}
                deadline={opportunity.deadline}
                requirements={opportunity.requirements}
                remoteAllowed={opportunity.remote_allowed}
                type={opportunity.type}
                experienceLevel={opportunity.experience_level}
                lookingFor={opportunity.looking_for}
              />

              {opportunity.organization && (
                <OrganizationInfo organization={opportunity.organization} />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};